import {
  AccountContactMethodsDocument,
  ContactMethodChannel,
  ContactMethodFragment,
  CreateOrUpdateContactMethodMutationVariables,
  useCreateOrUpdateContactMethodMutation,
} from 'graphql/bindings/AdminBindings'
import { Box, FlagField, FormModal, FormSectionTitle, SpinnerButton, TextField } from '@forgeglobal/design-framework'
import { ContactMethodField } from 'components/shared/dropdowns/ContactMethodField'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { MutationResult } from 'react-apollo'
import React, { Fragment } from 'react'
import pick from 'lodash/pick'

interface FormShape {
  id?: string
  account_id: string
  channel?: { label: string; value: ContactMethodChannel }
  notes?: string
  is_primary: boolean
  channel_value?: string
  mutationResult: MutationResult
}

const getLabel = (channel: ContactMethodChannel) => {
  switch (channel) {
    case ContactMethodChannel.Email:
      return 'Email address'
    case ContactMethodChannel.Phone:
      return 'Phone number'
    case ContactMethodChannel.Linkedin:
      return 'LinkedIn URL'
    case ContactMethodChannel.Wechat:
      return 'WeChat'
    case ContactMethodChannel.Whatsapp:
      return 'WhatsApp'
  }
}

const DetailsForm: React.FC<FormRenderProps<FormShape>> = ({ handleSubmit, values, initialValues }) => {
  const mutateInProgress = values.mutationResult.called
  return (
    <form onSubmit={handleSubmit}>
      <FormSectionTitle>Method Details</FormSectionTitle>
      <ContactMethodField name="channel" label="Contact method (required)" />
      {values.channel && (
        <Fragment>
          <Field name="channel_value" label={getLabel(values.channel.value)} component={TextField} fullWidth />
          <Field
            name="is_primary"
            label={`Primary ${getLabel(values.channel.value)}`}
            component={FlagField}
            disabled={initialValues.is_primary}
            fullWidth
          />
        </Fragment>
      )}
      <Box mt={2}>
        <SpinnerButton
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          loading={mutateInProgress}
          caption="Save"
        />
      </Box>
    </form>
  )
}

export const AccountContactInformationModal: React.FC<{
  hideModal: () => void
  contactMethod?: ContactMethodFragment
  accountId: string
}> = ({ hideModal, contactMethod, accountId }) => {
  const [mutate, mutateResult] = useCreateOrUpdateContactMethodMutation({
    onCompleted: hideModal,
    refetchQueries: [{ query: AccountContactMethodsDocument, variables: { id: accountId } }],
  })
  const channel = contactMethod?.channel
  const initialValues: FormShape = {
    id: contactMethod?.id,
    account_id: accountId,
    is_primary: false,
    ...(contactMethod as any),
    channel: !!channel && { label: getLabel(channel), value: channel },
    mutationResult: mutateResult,
  }
  const submit = (values: FormShape) => {
    mutate({
      variables: {
        form: {
          ...pick(values, ['id', 'account_id', 'notes', 'is_primary', 'channel_value']),
          channel: values.channel?.value,
        },
      } as CreateOrUpdateContactMethodMutationVariables,
    })
  }
  return (
    <FormModal open hideModal={hideModal} title={`${contactMethod ? 'Edit' : 'Create'}: Contact Method`}>
      <Form<FormShape>
        component={DetailsForm}
        onSubmit={submit}
        initialValues={{
          ...initialValues,
        }}
      />
    </FormModal>
  )
}
